import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtNum, fmtDate } from "../lib/api";
import {
  TrendingUp,
  TrendingDown,
  Trophy,
  Users,
  Calendar,
  CalendarOff,
  Wallet,
  Percent,
  Sparkles,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Printer,
} from "lucide-react";
import { exportCSV, printPDF, fmtINRPlain } from "../lib/export";

const WINDOWS = [
  { v: 7, label: "Last 7 days" },
  { v: 30, label: "Last 30 days" },
  { v: 90, label: "Last 90 days" },
];

const Stat = ({ label, value, sub, icon: Icon, tone = "default", testid }) => {
  const toneCls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50"
      : "border-zinc-200 bg-white";
  return (
    <div data-testid={testid} className={`border ${toneCls} p-5`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">
          {label}
        </div>
        <Icon className="h-4 w-4 text-zinc-400" />
      </div>
      <div className="mt-3 font-display text-2xl font-bold tracking-tight font-mono-num">
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-zinc-600">{sub}</div>}
    </div>
  );
};

const GrowthPill = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const positive = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 ${
        positive ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
      }`}
    >
      {positive ? (
        <ArrowUpRight className="h-3 w-3" />
      ) : (
        <ArrowDownRight className="h-3 w-3" />
      )}
      {fmtNum(Math.abs(pct), 1)}%
    </span>
  );
};

const Bar = ({ pct, color = "bg-sky-600" }) => (
  <div className="h-2 bg-zinc-100 overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} />
  </div>
);

export default function Insights() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = async () => {
    const res = await api.get(`/reports/insights?days=${days}`);
    setData(res.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [days]);

  if (!data) {
    return (
      <div className="p-8 text-zinc-500" data-testid="insights-loading">
        Loading insights…
      </div>
    );
  }

  const h = data.headline;
  const cm = data.channel_mix;
  const cat = data.category_mix;
  const catPct = data.category_pct;
  const totalChannelAmt = cm.pos.amount + cm.wholesale.amount;
  const posShare = totalChannelAmt > 0 ? (cm.pos.amount / totalChannelAmt) * 100 : 0;
  const wsShare = 100 - posShare;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Sparkles className="h-3 w-3" /> Analytics
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Insights
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-2 border border-zinc-200 bg-white p-1">
            {WINDOWS.map((w) => (
              <button
                key={w.v}
                onClick={() => setDays(w.v)}
                data-testid={`insights-window-${w.v}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider ${
                  days === w.v
                    ? "bg-zinc-950 text-white"
                    : "text-zinc-600 hover:bg-zinc-100"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              exportCSV(
                `insights_${days}d.csv`,
                [
                  { key: "metric", label: "Metric" },
                  { key: "value", label: "Value" },
                ],
                [
                  { metric: `Window (days)`, value: data.window_days },
                  { metric: "Sales", value: data.headline.sales_total.toFixed(2) },
                  { metric: "Purchase Cost", value: data.headline.purchase_total.toFixed(2) },
                  { metric: "Expenses", value: (data.headline.expense_total || 0).toFixed(2) },
                  { metric: "Profit", value: data.headline.profit.toFixed(2) },
                  { metric: "Margin %", value: data.headline.margin_pct },
                  { metric: "Growth %", value: data.headline.growth_pct ?? "" },
                  { metric: "Total Bills", value: data.headline.total_bills },
                  { metric: "Avg Bill", value: data.headline.avg_bill.toFixed(2) },
                  { metric: "POS Revenue", value: data.channel_mix.pos.amount.toFixed(2) },
                  { metric: "Wholesale Revenue", value: data.channel_mix.wholesale.amount.toFixed(2) },
                  { metric: "Outstanding Receivables", value: data.receivables.outstanding.toFixed(2) },
                  ...data.top_products.map((p, i) => ({ metric: `Top Product #${i+1}`, value: `${p.product_name} - ${fmtINRPlain(p.amount)}` })),
                  ...data.top_customers.map((c, i) => ({ metric: `Top Customer #${i+1}`, value: `${c.name} - ${fmtINRPlain(c.amount)}` })),
                ]
              );
            }}
            data-testid="insights-export-csv"
            className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100 flex items-center gap-1"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
          <button
            onClick={() => {
              const h = data.headline;
              const body = `<h1>EEMAN MEATS</h1><div class="sub">Insights Report · Last ${data.window_days} days · ${fmtDate(data.as_of)}</div>
              <div class="kpi-grid">
                <div class="kpi"><div class="lbl">Sales</div><div class="val">${fmtINRPlain(h.sales_total)}</div></div>
                <div class="kpi"><div class="lbl">Purchase</div><div class="val">${fmtINRPlain(h.purchase_total)}</div></div>
                <div class="kpi"><div class="lbl">Expenses</div><div class="val">${fmtINRPlain(h.expense_total||0)}</div></div>
                <div class="kpi"><div class="lbl">Profit (${h.margin_pct}%)</div><div class="val">${fmtINRPlain(h.profit)}</div></div>
              </div>
              <div class="section">Channel Mix</div>
              <table><tr><td>Retail POS</td><td class="right">${fmtINRPlain(data.channel_mix.pos.amount)} (${data.channel_mix.pos.count} bills)</td></tr>
              <tr><td>Wholesale</td><td class="right">${fmtINRPlain(data.channel_mix.wholesale.amount)} (${data.channel_mix.wholesale.count} orders)</td></tr></table>
              <div class="section">Top Products</div>
              <table><thead><tr><th>#</th><th>Product</th><th class="right">Revenue</th></tr></thead><tbody>${data.top_products.map((p,i)=>`<tr><td>${i+1}</td><td>${p.product_name}</td><td class="right">${fmtINRPlain(p.amount)}</td></tr>`).join("")}</tbody></table>
              <div class="section">Top Customers</div>
              <table><thead><tr><th>#</th><th>Customer</th><th class="right">Revenue</th></tr></thead><tbody>${data.top_customers.map((c,i)=>`<tr><td>${i+1}</td><td>${c.name}</td><td class="right">${fmtINRPlain(c.amount)}</td></tr>`).join("")}</tbody></table>
              <div class="section">Outstanding</div>
              <table><tr><td>Billed</td><td class="right">${fmtINRPlain(data.receivables.total_billed_wholesale)}</td></tr>
              <tr><td>Collected</td><td class="right">${fmtINRPlain(data.receivables.total_collected)}</td></tr>
              <tr><td><b>Outstanding</b></td><td class="right"><b>${fmtINRPlain(data.receivables.outstanding)}</b></td></tr></table>`;
              printPDF(`Insights ${days}d`, body);
            }}
            data-testid="insights-export-pdf"
            className="bg-zinc-900 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-800 flex items-center gap-1"
          >
            <Printer className="h-3 w-3" /> PDF
          </button>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 stagger">
        <Stat
          label="Sales"
          value={fmtINR(h.sales_total)}
          sub={
            <span className="flex items-center gap-2">
              {h.total_bills} bills <GrowthPill pct={h.growth_pct} />
            </span>
          }
          icon={Receipt}
          testid="kpi-sales"
        />
        <Stat
          label="Purchase Cost"
          value={fmtINR(h.purchase_total)}
          sub={`Avg ${fmtINR(h.purchase_total / days)}/day`}
          icon={Wallet}
          testid="kpi-purchase"
        />
        <Stat
          label="Profit"
          value={fmtINR(h.profit)}
          sub={`${fmtNum(h.margin_pct, 1)}% margin · ${fmtINR(h.avg_daily_profit)}/day`}
          icon={h.profit >= 0 ? TrendingUp : TrendingDown}
          tone={h.profit >= 0 ? "good" : "bad"}
          testid="kpi-profit"
        />
        <Stat
          label="Average Bill"
          value={fmtINR(h.avg_bill)}
          sub={`${fmtINR(h.avg_daily_sales)} per day`}
          icon={Percent}
          testid="kpi-avg-bill"
        />
      </div>

      {/* Channel Mix */}
      <div className="border border-zinc-200 bg-white p-5 mb-3" data-testid="channel-mix">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          Channel Mix
        </div>
        <div className="font-display text-lg font-semibold mb-4">
          Where your revenue comes from
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-sm font-medium">Retail POS</span>
              <span className="font-mono-num text-sm font-bold">
                {fmtINR(cm.pos.amount)}{" "}
                <span className="text-zinc-500 font-normal text-xs">
                  · {fmtNum(posShare, 0)}%
                </span>
              </span>
            </div>
            <Bar pct={posShare} color="bg-sky-600" />
            <div className="text-[10px] text-zinc-500 mt-1">
              {cm.pos.count} bills · avg {fmtINR(cm.pos.avg_bill)}
            </div>
          </div>
          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-sm font-medium">Wholesale</span>
              <span className="font-mono-num text-sm font-bold">
                {fmtINR(cm.wholesale.amount)}{" "}
                <span className="text-zinc-500 font-normal text-xs">
                  · {fmtNum(wsShare, 0)}%
                </span>
              </span>
            </div>
            <Bar pct={wsShare} color="bg-zinc-900" />
            <div className="text-[10px] text-zinc-500 mt-1">
              {cm.wholesale.count} orders · avg {fmtINR(cm.wholesale.avg_bill)}
            </div>
          </div>
        </div>
      </div>

      {/* Category Mix */}
      <div className="border border-zinc-200 bg-white p-5 mb-3" data-testid="category-mix">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          Product Categories
        </div>
        <div className="font-display text-lg font-semibold mb-4">
          What sells most
        </div>
        <div className="space-y-3">
          {Object.entries(cat).map(([k, v]) => (
            <div key={k}>
              <div className="flex justify-between text-sm mb-1">
                <span className="capitalize font-medium">{k}</span>
                <span className="font-mono-num">
                  {fmtINR(v)}{" "}
                  <span className="text-zinc-500 text-xs">· {catPct[k]}%</span>
                </span>
              </div>
              <Bar pct={catPct[k]} color={
                k === "chicken" ? "bg-sky-600" : k === "mutton" ? "bg-rose-600" : "bg-amber-500"
              } />
            </div>
          ))}
        </div>
      </div>

      {/* Top Products & Customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div className="border border-zinc-200 bg-white p-5" data-testid="top-products">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-1">
            <Trophy className="h-3 w-3" /> Top 5 Products
          </div>
          <div className="font-display text-lg font-semibold mb-3">By revenue</div>
          {data.top_products.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No sales yet</div>
          ) : (
            <table className="w-full">
              <tbody>
                {data.top_products.map((p, i) => (
                  <tr key={p.product_id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-2 text-xs text-zinc-400 font-mono-num w-6">
                      #{i + 1}
                    </td>
                    <td className="py-2 text-sm font-medium">{p.product_name}</td>
                    <td className="py-2 text-xs text-zinc-500 text-right">
                      {p.unit === "piece"
                        ? `${Math.round(p.qty)} pcs`
                        : `${fmtNum(p.qty, 2)} kg`}
                    </td>
                    <td className="py-2 text-sm font-mono-num text-right font-semibold">
                      {fmtINR(p.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border border-zinc-200 bg-white p-5" data-testid="top-customers">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 flex items-center gap-1">
            <Users className="h-3 w-3" /> Top 5 Wholesale Customers
          </div>
          <div className="font-display text-lg font-semibold mb-3">By revenue</div>
          {data.top_customers.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              No wholesale orders yet
            </div>
          ) : (
            <table className="w-full">
              <tbody>
                {data.top_customers.map((c, i) => (
                  <tr key={c.customer_id} className="border-b border-zinc-100 last:border-b-0">
                    <td className="py-2 text-xs text-zinc-400 font-mono-num w-6">
                      #{i + 1}
                    </td>
                    <td className="py-2 text-sm font-medium">{c.name}</td>
                    <td className="py-2 text-xs text-zinc-500 text-right">
                      {c.orders} orders
                    </td>
                    <td className="py-2 text-sm font-mono-num text-right font-semibold">
                      {fmtINR(c.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Best & Worst Day + Receivables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-emerald-200 bg-emerald-50 p-5" data-testid="best-day">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Best Day
            </div>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          {data.best_day ? (
            <>
              <div className="font-display text-2xl font-bold font-mono-num">
                {fmtINR(data.best_day.amount)}
              </div>
              <div className="text-xs text-emerald-800 mt-1">{fmtDate(data.best_day.date)}</div>
            </>
          ) : (
            <div className="text-sm text-zinc-500">—</div>
          )}
        </div>

        <div className="border border-rose-200 bg-rose-50 p-5" data-testid="worst-day">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-800 flex items-center gap-1">
              <CalendarOff className="h-3 w-3" /> Slowest Day
            </div>
            <TrendingDown className="h-4 w-4 text-rose-600" />
          </div>
          {data.worst_day ? (
            <>
              <div className="font-display text-2xl font-bold font-mono-num">
                {fmtINR(data.worst_day.amount)}
              </div>
              <div className="text-xs text-rose-800 mt-1">{fmtDate(data.worst_day.date)}</div>
            </>
          ) : (
            <div className="text-sm text-zinc-500">—</div>
          )}
        </div>

        <div className="border border-zinc-200 bg-white p-5" data-testid="receivables-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
              <Wallet className="h-3 w-3" /> Outstanding (All Customers)
            </div>
          </div>
          <div
            className={`font-display text-2xl font-bold font-mono-num ${
              data.receivables.outstanding > 0 ? "text-rose-700" : "text-emerald-700"
            }`}
          >
            {fmtINR(data.receivables.outstanding)}
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            Billed {fmtINR(data.receivables.total_billed_wholesale)} · Collected{" "}
            {fmtINR(data.receivables.total_collected)}
          </div>
        </div>
      </div>

      {/* Smart observations */}
      <div className="mt-6 border border-sky-200 bg-sky-50 p-5" data-testid="smart-tips">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sky-800 mb-2 flex items-center gap-1">
          <Sparkles className="h-3 w-3" /> Quick Observations
        </div>
        <ul className="text-sm text-sky-900 space-y-1.5 list-disc pl-5">
          {h.growth_pct !== null && (
            <li>
              Sales {h.growth_pct >= 0 ? "grew" : "fell"} by{" "}
              <b>{fmtNum(Math.abs(h.growth_pct), 1)}%</b> vs previous {days} days.
            </li>
          )}
          {totalChannelAmt > 0 && (
            <li>
              <b>{posShare >= wsShare ? "Retail POS" : "Wholesale"}</b> is your dominant
              channel at <b>{fmtNum(Math.max(posShare, wsShare), 0)}%</b> of revenue.
            </li>
          )}
          {h.margin_pct < 10 && h.sales_total > 0 && (
            <li>
              Profit margin is <b>{fmtNum(h.margin_pct, 1)}%</b> — consider revising
              daily rates or controlling wastage.
            </li>
          )}
          {data.receivables.outstanding > 0 && (
            <li>
              <b>{fmtINR(data.receivables.outstanding)}</b> outstanding from wholesale
              customers — review Customers page to collect.
            </li>
          )}
          {data.top_products[0] && (
            <li>
              Top earner: <b>{data.top_products[0].product_name}</b> generated{" "}
              <b>{fmtINR(data.top_products[0].amount)}</b>.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
